import React from 'react';
import { Link } from 'react-router-dom';
import { Wind, RefreshCw, ShieldCheck, Mail, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Returns = () => {
  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-primary/80 backdrop-blur-lg border-b border-gold/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Wind className="w-8 h-8 text-gold" />
            <span className="text-2xl font-bold text-gold">MADTOYS</span>
          </Link>
          <Link to="/" className="text-gray-300 hover:text-gold transition-colors">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <h1 className="text-5xl font-bold text-white mb-6">Returns & Exchange Policy</h1>
          <p className="text-xl text-gray-400 mb-12">We want you to love your purchase. If you're not satisfied, we're here to help.</p>

          <div className="space-y-8">
            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <RefreshCw className="w-6 h-6 text-gold" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-3">7-Day Easy Returns</h2>
                    <p className="text-gray-300 leading-relaxed">
                      We offer a <span className="text-gold font-semibold">7-day return policy</span> from the date of delivery. 
                      If you're not completely satisfied with your MADTOYS Windmill Night Light, you can return it for a 
                      full refund or exchange.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Return Eligibility</h2>
                <p className="text-gray-300 mb-4">To be eligible for a return, your item must meet the following conditions:</p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <span className="text-gold mt-1">✓</span>
                    <span>Product must be unused and in the same condition as received</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-gold mt-1">✓</span>
                    <span>Original packaging must be intact</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-gold mt-1">✓</span>
                    <span>All accessories, batteries, and cables must be included</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-gold mt-1">✓</span>
                    <span>Return request must be initiated within 7 days of delivery</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">How to Initiate a Return</h2>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold text-dark-primary flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gold mb-1">Contact Us</h3>
                      <p className="text-gray-300">Email us at <span className="text-gold">info@madtoys.in</span> with your order number and reason for return</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold text-dark-primary flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gold mb-1">Get Approval</h3>
                      <p className="text-gray-300">We'll review your request and provide return instructions within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold text-dark-primary flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gold mb-1">Ship the Product</h3>
                      <p className="text-gray-300">Pack the item securely and ship it to the address provided</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold text-dark-primary flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gold mb-1">Get Your Refund</h3>
                      <p className="text-gray-300">Once we receive and inspect the item, your refund will be processed within 5-7 business days</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <ShieldCheck className="w-6 h-6 text-gold" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-3">Refund Method</h2>
                    <p className="text-gray-300 leading-relaxed">
                      Refunds will be processed to the original payment method used during purchase. 
                      For prepaid orders, the amount will be credited to your bank account or card. 
                      For Cash on Delivery orders, we'll initiate a bank transfer (please provide your bank details).
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-gold" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-3">Damaged or Defective Products</h2>
                    <p className="text-gray-300 leading-relaxed mb-3">
                      If you receive a damaged or defective product, please contact us immediately with photos of:
                    </p>
                    <ul className="space-y-2 text-gray-300 ml-4">
                      <li className="flex items-start gap-2">
                        <span className="text-gold">•</span>
                        <span>The damaged product</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gold">•</span>
                        <span>The packaging</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gold">•</span>
                        <span>The shipping label</span>
                      </li>
                    </ul>
                    <p className="text-gray-300 mt-3">
                      We'll arrange for a replacement or full refund at no additional cost to you.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-3">Need Help?</h2>
                    <p className="text-gray-300 leading-relaxed">
                      If you have any questions about our returns policy or need assistance with a return, 
                      please contact our customer support team:
                    </p>
                    <div className="mt-4 space-y-2 text-gray-300">
                      <p><span className="text-gold font-semibold">Email:</span> info@madtoys.in</p>
                      <p><span className="text-gold font-semibold">Support Hours:</span> 10 AM - 6 PM IST (Monday to Saturday)</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Returns;
